import type { Block, ListEntry, Section } from "@/components/legal/LegalContent";
import { isKnowledgeObject, seededKnowledgeData } from "./public-knowledge-template";

export interface LiveLegalContent {
  title?: string;
  seo_description?: string;
  last_updated?: string;
  intro?: Block[];
  sections?: Section[];
  jsonLd?: Record<string, unknown>;
}
function isListEntry(value: unknown, depth = 0): value is ListEntry {
  return typeof value === "string" || depth < 16 && isKnowledgeObject(value) &&
    typeof value.text === "string" && (value.items === undefined ||
      Array.isArray(value.items) && value.items.every(item => isListEntry(item, depth + 1)));
}
function isBlock(value: unknown): value is Block {
  if (!isKnowledgeObject(value) || Object.keys(value).length !== 1) return false;
  if (["p", "strong", "h3", "note"].some(key => typeof value[key] === "string")) return true;
  if (["ul", "ol"].some(key => Array.isArray(value[key]) && value[key].every(item => isListEntry(item)))) return true;
  return isKnowledgeObject(value.img) && typeof value.img.src === "string" &&
    typeof value.img.alt === "string" && (value.img.caption === undefined || typeof value.img.caption === "string");
}
export function isLiveLegalContent(value: unknown): value is LiveLegalContent {
  return isKnowledgeObject(value) &&
    ["title", "seo_description", "last_updated"].every(key => value[key] === undefined || typeof value[key] === "string") &&
    (value.intro === undefined || Array.isArray(value.intro) && value.intro.every(isBlock)) &&
    (value.sections === undefined || Array.isArray(value.sections) && value.sections.every(section =>
      isKnowledgeObject(section) && typeof section.id === "string" && typeof section.title === "string" &&
      Array.isArray(section.body) && section.body.every(isBlock))) &&
    (value.jsonLd === undefined || isKnowledgeObject(value.jsonLd));
}
/** Only public render fields enter the snapshot; null is a successful read of
 * no live override, never a substitution for an unknown/failed read. */
export function publicLegalContent(value: unknown): LiveLegalContent | null {
  if (value === null) return null;
  if (!isLiveLegalContent(value)) throw Object.assign(Error("Invalid required legal content"), { status: 503 });
  return Object.fromEntries(["title", "seo_description", "last_updated", "intro", "sections", "jsonLd"]
    .filter(key => value[key as keyof LiveLegalContent] !== undefined)
    .map(key => [key, value[key as keyof LiveLegalContent]]));
}
export function frozenLegalContent(seed: unknown, pageKey: string, locale: string) {
  if (!["privacy", "terms"].includes(pageKey) || locale !== "en") return null;
  const view = seededKnowledgeData<{ page: string; legalContent: LiveLegalContent | null }>(
    seed, "static-page", `/${pageKey}`, (data): data is { page: string; legalContent: LiveLegalContent | null } =>
      isKnowledgeObject(data) && data.page === pageKey && Object.prototype.hasOwnProperty.call(data, "legalContent") &&
      (data.legalContent === null || isLiveLegalContent(data.legalContent)));
  return view ? { value: view.legalContent } : null;
}
